/**
 * Duplicate Keyword Guard — карточка предложения и строки истории.
 * Макет: `Duplicate Keyword Guard.dc.html`, экран «Autopilot».
 *
 * Строки — в порядке §5 (спенд ↓) и НЕ переупорядочиваются: победитель
 * помечен флагом и бейджем, не позицией (ТЗ §8.1). Две копии — Swap, три и
 * больше — радио «Choose winner». Never suggest пишет все пары состава (§8.4).
 */

import { useMemo, useState } from "react";
import {
  type AutopilotSuggestion,
  type DkgCardRow,
  useApplySuggestionWithWinner,
  useApps,
  useDismissSuggestion,
} from "@/lib/api-hooks";
import { DKG_PRESET, ago, dkgSkipText } from "@/lib/dkg-history";


function money(v: number | null | undefined): string {
  return v == null ? "—" : `$${Number(v).toFixed(2)}`;
}

const swapIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4m0 0L3 8m4-4 4 4" /><path d="M17 8v12m0 0 4-4m-4 4-4-4" /></svg>
);

export function DkgCard({ s }: { s: AutopilotSuggestion }) {
  const rows: DkgCardRow[] = s.payload.rows ?? [];
  const proposed = s.payload.user_winner_keyword_id ?? s.payload.winner_keyword_id ?? rows[0]?.keyword_id ?? null;
  const [winner, setWinner] = useState<number | null>(proposed);
  const [confirm, setConfirm] = useState(false);
  const apply = useApplySuggestionWithWinner();
  const dismiss = useDismissSuggestion();
  const appsQ = useApps();
  const appName = useMemo(
    () => (appsQ.data ?? []).find((a) => a.id === s.app_id)?.name ?? "",
    [appsQ.data, s.app_id],
  );
  const busy = apply.isPending || dismiss.isPending;
  const choose = rows.length > 2;
  const cols =
    (choose ? "24px " : "") +
    "minmax(0,1.2fr) minmax(0,.9fr) minmax(0,1.5fr) minmax(0,.55fr) minmax(0,1fr) minmax(0,.85fr)";
  const ruleName = s.rule_name_snapshot ?? `Rule #${s.rule_id}`;
  const winText = rows.find((r) => r.keyword_id === winner)?.text ?? rows[0]?.text ?? "";

  const swap = () => {
    if (rows.length !== 2 || winner == null) return;
    setWinner(rows[0].keyword_id === winner ? rows[1].keyword_id : rows[0].keyword_id);
  };

  return (
    <div className="sg-card" data-state={s.status} data-preset={s.preset}>
      <div className="sg-src">
        <span className="sg-src-mark">{DKG_PRESET} · Suggestion</span>
        <span className="sg-rule">{ruleName}</span>
        <span className="sg-age">{ago(s.created_at)}</span>
      </div>

      <div className="dkg-head">
        <span className="dkg-head-app">{appName || s.search_term}</span>
        <span className="sg-cc" style={{ marginLeft: 0 }}>{s.country}</span>
        <span className="dkg-copies">· {rows.length} copies of the same exact keyword</span>
      </div>

      <div className="dkg-table" style={{ "--dkg-cols": cols } as React.CSSProperties}>
        <div className="dkg-th">
          {choose && <span />}
          <span>Campaign</span>
          <span>Ad group</span>
          <span>Keyword</span>
          <span>Spend 7d</span>
          <span>Page</span>
          <span className="is-right">Outcome</span>
        </div>
        {rows.map((r) => {
          const keeps = r.keyword_id === winner;
          // Пометка «variant of» — всегда, когда текст копии не совпадает с
          // текстом ПОБЕДИТЕЛЯ буквально (ТЗ §11.2); победитель меняется
          // здесь, поэтому сравнение живое, а не из payload.
          const variant = !keeps && r.text !== winText ? winText : null;
          return (
            <div
              key={r.keyword_id}
              className="dkg-tr"
              data-keeps={keeps ? "true" : "false"}
              data-pick={choose ? "true" : "false"}
              onClick={choose ? () => setWinner(r.keyword_id) : undefined}
            >
              {choose && (
                <span className="dkg-radio"><i /></span>
              )}
              <span className="dkg-cell dkg-cell-campaign" title={r.campaign_name ?? undefined}>
                {r.campaign_name ?? `#${r.campaign_id}`}
              </span>
              <span className="dkg-cell dkg-cell-ag" title={r.ad_group_name ?? undefined}>
                {r.ad_group_name ?? `#${r.ad_group_id}`}
              </span>
              <span className="dkg-cell-kw">
                <span className="dkg-kw">{r.text}</span>
                {variant && (
                  <span
                    className="dkg-variant"
                    title="Apple serves these keywords on the same queries, so the copies compete with each other."
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
                    variant of &quot;{variant}&quot;
                  </span>
                )}
              </span>
              <span className="dkg-spend">{money(r.spend_7d)}</span>
              <span className="dkg-cell dkg-page" title={r.page ?? undefined}>{r.page ?? "Default"}</span>
              <span className="dkg-outcome">
                <span className={"dkg-badge " + (keeps ? "is-keep" : "is-pause")}>
                  {keeps ? "Keeps running" : "Will be paused"}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      {choose && (
        <div className="dkg-pick-hint">
          Pick the row that should keep running — the rest move to “Will be paused”.
        </div>
      )}

      {apply.data?.status === "skipped" && (
        <div className="sg-result is-neg">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
          <span>{dkgSkipText(apply.data.skip_reason)}</span>
        </div>
      )}
      {apply.data?.error && (
        <div className="sg-result is-neg"><span>{apply.data.error}</span></div>
      )}

      {!confirm ? (
        <div className="sg-actions">
          <button className="sg-btn is-ghost" disabled={busy} onClick={() => setConfirm(true)}>
            Never suggest
          </button>
          {!choose && (
            <button className="sg-btn dkg-swap" disabled={busy} onClick={swap}>
              {swapIcon}
              Swap
            </button>
          )}
          <button
            className="sg-btn is-primary"
            disabled={busy || winner == null}
            onClick={() => apply.mutate({ id: s.id, winner_keyword_id: winner })}
          >
            {apply.isPending ? "Applying…" : "Apply"}
          </button>
        </div>
      ) : (
        <div className="dkg-confirm">
          <div className="dkg-confirm-txt">
            <b>Never suggest this duplicate again?</b>
            <span>
              {choose
                ? `Every combination of these rows is suppressed for ${ruleName}. Suppression is reset only by recreating the rule.`
                : `This pair is suppressed for ${ruleName}. Suppression is reset only by recreating the rule.`}
            </span>
          </div>
          <div className="dkg-confirm-acts">
            <button className="sg-btn" onClick={() => setConfirm(false)}>Cancel</button>
            <button className="sg-btn is-danger" disabled={busy} onClick={() => dismiss.mutate(s.id)}>
              Never suggest
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
